"use client"
import styled from "@emotion/styled"
import { Modal } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { Map, MapCameraChangedEvent, useMap } from "@vis.gl/react-google-maps"
import {
    Attribute,
    AutocompleteType,
    LocatorStationsWrapper,
    Station,
    TableDto,
    postPointOfInterstsExportSearchResults
} from "api"
import { LocationContext } from "contexts/LocationProvider"
import { StationMapRequest, postMapStations } from "lib/api"
import { generatePDF } from "lib/generatePDF"
import { Suspense, useContext, useEffect, useRef, useState } from "react"

import { InstallationStationsSearchRequest } from "api/api/APIOverride/PazInterfaces"
import { LayoutContext } from "contexts/LayoutProvider"
import FileSaver from "file-saver"
import { useRouter, useSearchParams } from "next/navigation"
import { trackEventName } from "reusable/ChargeGTM"
import {
    Desktop,
    Mobile,
    useIsDesktop
} from "ui"
import StyledAdvancedMarker from "./AdvancedMarker"
import ItemsList from "./ItemsList"
import LocationsSwiper from "./LocationsSwiper"
import SelectedDetails from "./SelectedDetails"
import SideActions from "./SideActions"
import TopActions, { Actions } from "./TopActions"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`
const StyledMapContainer = styled.div`
  position: relative;
  width: 100%;
  height: inherit;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 670px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 860px;
  }
`
const StyledStationCarousel = styled.div`
  position: absolute;
  bottom: 10px;
  left: 55px;
   z-index: 2;
   width: calc(100% - 60px);
`

const StyledGrid = styled.div`
  position: relative;
  display: grid;
  height: 540px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 320px 1fr;
     height: auto;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    grid-template-columns: 408px 1fr;
     // height: auto;
  }
`
const ModalCont = styled.div`
  background-color: white;
  height: 80%;
  width: 100%;
  padding: 20px;
  border-radius: 20px 20px 0 0;
  position: absolute;
  bottom: 0;
  overflow-y: scroll;
`

type Props = {
    metadata: Attribute[]
    stations: LocatorStationsWrapper
    isPazCharge: boolean
    withoutBreadCrumbs: boolean
    initialFiltersAreReady?: boolean
}

export interface UserLocation {
    lat: number;
    lon: number;
}

const MIN_ZOOM_TO_SHOW_LABEL = 13

function LocatorMap({ metadata, stations, isPazCharge, initialFiltersAreReady = true }: Props) {
    const {
        setSelectedLocation,
        showAsList,
        filters,
        selectedLocation,
        selectedCity,
        setMobileDetails,
        mobileDetails,
        searchValue,
        setSearchValue,
        queryParams,
        initialValueFilters,
        setIsMapLoaded,
        isMapLoaded,
        updateFilters,
        resetFilters
    } = useContext(LocationContext)
    const { isApiReady } = useContext(LayoutContext)
    const [userLoc, setUserLoc] = useState<UserLocation | null>(null)
    const [showStationCarousel, setShowCarousel] = useState(false)
    const [zoom, setZoom] = useState(12);
    const [list, setList] = useState<Station[]>([])
    const stationIdRef = useRef<number | null>(null)

    const handleClose = () => {
        setMobileDetails(false)
    }
    const searchParams = useSearchParams()
    const isDesktop = useIsDesktop()
    const map = useMap()
    const router = useRouter()

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) =>
                    setUserLoc({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
                // eslint-disable-next-line no-console
                (err) => console.log("Error fetching location:", err),
            )
        } else {
            // eslint-disable-next-line no-console
            console.log("Geolocation not supported")
        }
    }, [])

    const handleResponse = (responseData: LocatorStationsWrapper, isStation: boolean) => {
        const response = responseData.Body.body
        setList(isStation ? response.stations.reverse() : response.stations)
        if (response.geoCity) {// if there is selected city from search
            map?.setCenter({
                lat: response.geoCity.latitude,
                lng: response.geoCity.longitude,
            })
            map?.setZoom(MIN_ZOOM_TO_SHOW_LABEL)
        } else if (queryParams?.LocationLat || queryParams?.LocationLng) { // if there is selected station from search
            map?.setCenter({
                lat: queryParams.LocationLat || 31.9648616,
                lng: queryParams.LocationLng || 34.9004544,
            })
            map?.setZoom(MIN_ZOOM_TO_SHOW_LABEL)

        } else if (response?.stations.length > 0) {
            if (response?.stations.length > 0) {
                if (stationIdRef.current) {
                    return
                }
                const bounds = new window.google.maps.LatLngBounds();
                response.stations.forEach((item) => bounds.extend(new window.google.maps.LatLng(item.geoLocation.latitude, item.geoLocation.longitude)));
                map?.fitBounds(bounds);

                // Apply zoom constraints
                const maxZoom = 16;
                const minZoom = 9;
                const currentZoom = map?.getZoom() || zoom;
                if (currentZoom > maxZoom) {
                    map?.setZoom(maxZoom);
                } else if (currentZoom < minZoom) {
                    map?.setZoom(minZoom);
                }
            }
        }
    }
    const focusOnSpecificLocation = (lat: number, lon: number) => {
        map?.setCenter({
            lat: lat,
            lng: lon
        })
        map?.setZoom(16)
    }

    useQuery<LocatorStationsWrapper>({
        queryKey: ["filters", filters, selectedCity, queryParams],
        queryFn: async () => {
            const params: StationMapRequest = queryParams!
            const stationsRes = await postMapStations(params)
            const data = stationsRes

            const isSelectedStation = selectedCity?.type === AutocompleteType.Station
            handleResponse(data, isSelectedStation)
            return data
        },
        enabled: (!!filters || !!selectedCity) && initialFiltersAreReady && isApiReady,
        refetchOnWindowFocus: false,
        initialData: stations,
    })
    const onPrint = () => {
        const array = list.map((object) => Object.values(object)) || []
        const printArray: TableDto = {
            Header: { Columns: ["שם התחנה", "כתובת", " מאפייני תחנה "] },
            Rows: array.map((el) => ({
                Columns: [
                    el[3],
                    el[4],
                    el[16].map((item: any) => item?.name).join(" ,"),
                ],
            })),
            Footer: { Columns: [] },
            SubTitle: "",
            Title: "",
        }
        generatePDF([printArray])
        trackEventName("Map_print")
    }

    const onDownload = async () => {
        try {
            const reqBody: InstallationStationsSearchRequest = {
                SearchTerm: selectedCity?.name || searchValue,
                Attributes: [],
                InstallationProvider: "Orpak",
                MetaData: filters?.map(filter => filter.id) || [],
            }
            const response = await postPointOfInterstsExportSearchResults("", reqBody)
            if (response.data) {
                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                })
                // Use FileSaver to save the file
                FileSaver.saveAs(blob, "Reports.xlsx")
                trackEventName("Map_download")
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err)
        }
    }
    const getSelectedSiteFromUrl = () => {
        const siteId = searchParams.get("station")
        if (!siteId) {
            return
        }
        stationIdRef.current = parseInt(siteId)
        const siteFromUrl = stations.Body.body.stations.find(site => site.id === parseInt(siteId))

        if (!siteFromUrl) {
            return
        }
        if (isPazCharge) {
            // if the station from the url is not a charge station, redirect to service locator
            const isChargeStation = initialValueFilters?.some(filter => siteFromUrl?.metaData?.some(meta => meta.id === filter.id))
            if (!isChargeStation) {
                setTimeout(() => {
                    router.push("/service-locator/?station=" + siteId)
                }, 0)
                return
            }
        }
        setSelectedLocation(siteFromUrl)
    }
    const getSearchTermFromUrl = () => {
        const seatchTermUrl = searchParams.get("search")
        if (!seatchTermUrl) {
            return
        }
        setSearchValue(seatchTermUrl)
    }
    const getMetadataFromUrl = () => {
        const seatchTermUrl = new URLSearchParams(window.location.search);
        const metadataUrl = seatchTermUrl.get("metadata");
        if (!metadataUrl) {
            resetFilters()
            return
        }
        try {
            const attrs = JSON.parse(decodeURIComponent(metadataUrl));
            const metadataArray: Attribute[] = attrs.map((attr: Attribute) => {
                return {
                    id: attr
                }
            });
            updateFilters(metadataArray);
        } catch (error) {
            return;
        }
    };
    useEffect(() => {
        if (isMapLoaded && selectedLocation) {
            focusOnSpecificLocation(selectedLocation.geoLocation.latitude, selectedLocation.geoLocation.longitude)
        }
    }, [isMapLoaded])

    useEffect(() => {
        getSelectedSiteFromUrl();
        getSearchTermFromUrl();
        getMetadataFromUrl();
    }, [])

    const handleStaionClicked = (item: Station) => {
        setSelectedLocation(item)
        if (!isDesktop) {
            setShowCarousel(true)
        }
    }
    const handleZoomChagne = (event: MapCameraChangedEvent) => {
        setZoom(event.map.getZoom()!)
    }
    return (
        <StyledContainer>
            <Suspense>
                <TopActions
                    isPazCharge={isPazCharge}
                    metadata={metadata}
                    userLoc={userLoc}
                    locations={list}
                    onPrint={onPrint}
                    onDownload={onDownload}
                    focusOnSpecificLocation={focusOnSpecificLocation}
                    setShowCarousel={setShowCarousel}
                />
            </Suspense>
            <StyledGrid>
                <Desktop>
                    <SideActions
                        isPazCharge={isPazCharge}
                        metadata={metadata}
                        userLoc={userLoc}
                        locations={list}
                    />
                </Desktop>
                <>
                    <StyledMapContainer>
                        {showAsList ? (
                            <ItemsList list={list} />
                        ) : (
                            <Map
                                streetViewControl={false}
                                fullscreenControl={false}
                                mapId="811c65245afc97ba"
                                mapTypeControl={false}
                                onZoomChanged={handleZoomChagne}
                                defaultZoom={9}
                                defaultCenter={{
                                    lat: 31.9648616,
                                    lng: 34.9004544,
                                }}
                                onTilesLoaded={() => {
                                    setIsMapLoaded(true); // Mark the map as ready
                                }}
                            >
                                {list.length > 0 &&
                                    list?.map((item, key) => {
                                        const color = item.type === 1 ? "#FCBB00" : "#B30102"
                                        return (
                                            <StyledAdvancedMarker
                                                active={selectedLocation?.id === item.id}
                                                onClick={() => handleStaionClicked(item)}
                                                key={key.toString()}
                                                lng={item?.geoLocation?.longitude}
                                                lat={item?.geoLocation?.latitude}
                                                fill={color}
                                                title={item.name}
                                                showLabel={zoom >= MIN_ZOOM_TO_SHOW_LABEL}
                                            />
                                        )
                                    })}
                            </Map>
                        )}
                    </StyledMapContainer>
                    <Mobile>
                        {showStationCarousel &&
                            <StyledStationCarousel>
                                <LocationsSwiper locations={list} />
                            </StyledStationCarousel>}
                    </Mobile>
                </>
            </StyledGrid>
            <Mobile>
                <Actions onPrint={onPrint} onDownload={onDownload} />
                <Modal
                    open={mobileDetails}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <ModalCont>
                        <SelectedDetails handleClose={handleClose} />
                    </ModalCont>
                </Modal>
            </Mobile>
        </StyledContainer>
    )
}

export default LocatorMap
